import React from "react";
import { useHistory } from "react-router";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "features/authConfig";
import * as paths from "features/routing/paths";
import { LoginRedirectToast } from "features/patientDashboard/ErrorToast";
import { FullScreenLoader } from "components/FullScreenLoader/FullScreenLoader";

/**
 * This route exists to mimic clicking "Portal Login".
 *
 * /patient_dashboard/login  --> triggers MSAL loginRedirect
 * After successful login, MSAL returns to the app (redirect_uri),
 * and we route the user into the normal selector flow.
 *
 * IMPORTANT:
 * - Never call loginRedirect while MSAL is in progress
 * - Never spam loginRedirect on re-renders (attempt once per short window)
 */
const LOGIN_ATTEMPT_KEY = "ciam:portal_login_attempted_at";
const LOGIN_ATTEMPT_WINDOW_MS = 30_000;

function canAttemptPortalLoginOnce(): boolean {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(LOGIN_ATTEMPT_KEY) || "0");
  if (last && now - last < LOGIN_ATTEMPT_WINDOW_MS) return false;
  sessionStorage.setItem(LOGIN_ATTEMPT_KEY, String(now));
  return true;
}

const PatientDashboardLoginRedirect: React.FC = () => {
  const { instance, inProgress } = useMsal();
  const history = useHistory();
  const [showLoginToast, setShowLoginToast] = React.useState(false);

  const handleRetry = () => {
    sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
    setShowLoginToast(false);
    window.location.reload();
  };

  React.useEffect(() => {
    setShowLoginToast(false);

    // 1) If MSAL is busy, do nothing
    if (inProgress !== InteractionStatus.None) {
      setShowLoginToast(true);
      return;
    }

    // 2) If we have any accounts, set active + go to selector
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      if (!instance.getActiveAccount()) {
        instance.setActiveAccount(accounts[0]);
      }
      history.replace(paths.patientSelector());
      return;
    }

    // 3) Avoid infinite auto-redirect loops
    if (!canAttemptPortalLoginOnce()) {
      setShowLoginToast(true);
      return;
    }

    setShowLoginToast(false);

    // After login, push them into the selector route
    sessionStorage.setItem("postLoginRedirect", paths.patientSelector());

    instance.loginRedirect(loginRequest).catch((e) => {
      // eslint-disable-next-line no-console
      console.log("loginRedirect failed", e);
    });
  }, [instance, inProgress, history]);

  return (
    <>
      <FullScreenLoader />
      <LoginRedirectToast
        isLoading={showLoginToast}
        onDismiss={() => setShowLoginToast(false)}
        onRetry={handleRetry}
      />
    </>
  );
};

export default PatientDashboardLoginRedirect;
